<template>
  <div class="relative w-full h-full">
    <template v-if="requestSettings">
      <EditorDeckModalsNewFeatureFound
        v-if="requestSettings.status === 'not-setup'"
        :showing="true"
        :company-id="companyId"
        confined
        hide-close
        data-testid="new-feature-modal"
      />

      <template v-else>
        <EditorDeckSlidesEditorOverlay :show-top-bar="showMeetingSlideTopBar">
          <template #top-bar>
            <div class="flex items-center justify-between gap-x-1.5">
              <div class="flex items-center gap-x-1.5">
                <p class="mb-0" data-testid="editor-slide-booked">Meetings booked with:</p>
                <VHoverable
                  ref="HoverableRef"
                  :hover-status="hoverStatus"
                  :content="selectedUser.user.name"
                  data-testid="editor-slide-hover"
                >
                  <template #default>
                    <VDropdown
                      v-model="selectedUser.user"
                      size="sm"
                      :options="users as unknown as Option[]"
                      theme="dark"
                      container-classes="top-0 w-76 mt-3 left-0"
                      data-testid="editor-slide-dropdown"
                      @update:model-value="chooseMeetingAdmin($event)"
                      @toggle="toggleHoverStatus"
                    >
                      <template #current-single="{ item }">{{ item.name }}</template>
                      <template #option="{ item, selected }">
                        <VDropdownItem
                          :item="(item as unknown as ScheduleSetting).user.name"
                          :selected="selected"
                          variant="white"
                          :class="{ 'font-semibold': selected }"
                        />
                      </template>
                    </VDropdown>
                  </template>
                </VHoverable>
              </div>
              <VButton
                v-if="isCurrentUserSelected"
                :component="NuxtLink"
                target="_blank"
                :to="{ name: 'meetings-book-setup-meeting-details', query: { update: true, company_id: companyId } }"
                size="xs"
                data-testid="editor-slide-edit"
                class="flex"
              >
                <CogIcon class="w-4 h-4 mr-1" />Edit Settings
              </VButton>
            </div>
            <div v-if="requestStatus !== 'active'" class="mt-4">
              <VMessage
                v-if="requestStatus === 'incomplete' || requestStatus === 'not-setup'"
                class="items-center"
                :show="true"
                banner-type="warning"
                data-testid="editor-slide-incomplete"
                >Looks like you haven't set up your meeting booking tool. To use your availability, please set it up
                <template #dismiss>
                  <VButton
                    class="inline-block w-full text-center"
                    component="a"
                    :href="`/meetings/book/setup/intro?companyId=${companyId}`"
                    target="_blank"
                    size="sm"
                    no-shadow="noShadow"
                    no-border="noBorder"
                    @click="void 0"
                    >Set up this feature</VButton
                  >
                </template>
              </VMessage>
              <VMessage
                v-if="requestStatus === 'inactive' || requestStatus === 'none-available'"
                class="items-center"
                :show="true"
                banner-type="warning"
                data-testid="editor-slide-inactive"
                >Looks like your meeting booking tool is turned off. Please turn it on to use your availability
                <template #dismiss>
                  <VButton
                    class="w-full"
                    size="sm"
                    no-shadow="noShadow"
                    no-border="noBorder"
                    @click="
                      $emit('activate-book-a-meeting', (requestSettings as BookMeetingState | BookMeetingNoneAvailable)?.currentUser),
                        $emit('update:showing', false)
                    "
                    >Turn on</VButton
                  >
                </template>
              </VMessage>
            </div>
          </template>
        </EditorDeckSlidesEditorOverlay>
        <DeckMeetingSlide v-if="selectedUserSettings" :key="requestStatus" :request-setting-id="selectedUserSettings.id" />
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { computed, toRefs, watch } from 'vue'
import { CogIcon } from '@heroicons/vue/24/solid'

import type { Slide } from '@/entities/deck'
import type { ScheduleSetting } from '@/entities/meeting'
import type { BookMeetingStatus, BookMeetingState, BookMeetingNoneAvailable, BookMeetingIncomplete } from '@/composables/useBookMeeting'
import type { Option } from '@/components/VDropdown.vue'
import { isBAMNotSetup, isBAMSetupIncomplete, isBAMUnavailable } from '@/composables/useBookMeeting'

const NuxtLink = resolveComponent('NuxtLink')

const props = defineProps({
  slide: {
    type: Object as PropType<DeepReadonly<Slide>>,
    required: true,
  },
  showMeetingSlideTopBar: {
    type: Boolean,
    required: true,
  },
  requestSettings: {
    type: Object as PropType<DeepReadonly<BookMeetingStatus | null>>,
    default: null,
  },
  companyId: {
    type: Number,
    required: true,
  },
})

const emit = defineEmits(['activate-book-a-meeting', 'update:slide', 'update:showing'])

const HoverableRef = ref<HTMLDivElement | null>(null)
const { requestSettings, slide } = toRefs(props)
const { currentUser: selectedUser, users } = useBookMeetingDropdown(requestSettings)
const requestStatus = computed(() => requestSettings.value?.status)
const { hoverStatus, resetHoverStatus, toggleHoverStatus } = useHoverable(HoverableRef)

const selectedUserSettings = computed(() => {
  if (!requestSettings.value || isBAMNotSetup(requestSettings.value as BookMeetingStatus)) return slide.value.request_setting

  if (isBAMUnavailable(requestSettings.value as BookMeetingStatus)) return (requestSettings.value as BookMeetingNoneAvailable).currentUser

  const userSettings = (requestSettings.value as BookMeetingIncomplete).users.find(
    (user: ScheduleSetting) => user.id === selectedUser.value?.id,
  )

  return userSettings ?? slide.value.request_setting
})

const isCurrentUserSelected = computed(() => {
  if (requestSettings.value?.status !== 'active') return false
  return requestSettings.value?.currentUser?.id === selectedUser.value?.id
})

watch(
  [slide, requestSettings],
  ([slide, requestSettings]) => {
    if (!requestSettings || isBAMNotSetup(requestSettings as BookMeetingStatus)) return

    const currentSettings = isBAMSetupIncomplete(requestSettings as BookMeetingStatus)
      ? (requestSettings as BookMeetingIncomplete).users[0]
      : (requestSettings as BookMeetingNoneAvailable).currentUser

    selectedUser.value = (slide.request_setting || currentSettings) as ScheduleSetting
  },
  { immediate: true },
)

function chooseMeetingAdmin(admin: { id: number; name: string }): void {
  emit('update:slide', { id: slide.value.id, request_setting_id: admin.id })
  resetHoverStatus()
}
</script>
